import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Icon,
  Text,
  Flex,
  useColorMode,
  useColorStyle,
  Stack,
  Divider,
  Spinner,
  Alert,
} from '@tonic-one/react';
import { ChevronLeftIcon } from '@tonic-one/react-icons';
import { t } from 'i18next';
import { MitigationTable } from './components/MitigationTable';
import { MitigationType } from '../types/remediation';
import {
  EmailHeader,
  AnalysisResults,
  MitigationProgress,
  RemediationSection,
  EmailDrawer,
  MitigationAlert,
} from './components';
import { useAnalysisData } from './hooks/useAnalysisData';
import { MitigationHeader } from './components/MitigationHeader';
import { useNavigateWithHandler } from '@Utils/hooks';
import { OperationsApi } from '@Apis';

export const AnalysisRemediationDetails = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const navigate = useNavigateWithHandler();
  const { companyGuid, feedbackId } = useParams<{
    companyGuid: string;
    feedbackId: string;
  }>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<{
    columnId: string;
    direction: 'asc' | 'desc';
  }>({
    columnId: 'message_arrive_time',
    direction: 'desc',
  });

  // Use custom hook for data fetching and processing
  const {
    mitigationData,
    result,
    formattedTimestamp,
    mitigationDone,
    mitigationTotal,
    isResultLoading,
    isMailLoading,
    isMitigationLoading,
    mitigationError,
    mitigationNotRequired,
    mitigationsAvailable,
    executeFeedbackActionsMutation,
  } = useAnalysisData(companyGuid, feedbackId, pagination, sorting);

  // Check for remediation error state
  const remediationError = result.processingState === 'error';

  // Calculate tier 1 pending count for additional messages alert
  const tier1PendingCount = useMemo(() => {
    const tier1Stats = mitigationData?.statistic?.mitigationTier?.['1'];
    if (!tier1Stats) return 0;

    const pending = tier1Stats.totalAffectedCount - tier1Stats.doneCount;
    return pending > 0 ? pending : 0;
  }, [mitigationData?.statistic?.mitigationTier]);

  const handleExecuteActions = useCallback(
    (actionIds: string[]) => {
      if (actionIds.length > 0) {
        executeFeedbackActionsMutation.mutate(actionIds);
      }
    },
    [executeFeedbackActionsMutation]
  );

  const [selectableTotal, setSelectableTotal] = useState<number | undefined>();
  const fetchMitigationPage = useCallback(
    async (offset: number, limit: number) => {
      const response = await OperationsApi.apiGetFeedbackRelatedEntries(
        companyGuid || '',
        feedbackId || '',
        { offset, limit, sort: sorting.columnId, order: sorting.direction }
      );
      return response.data;
    },
    [companyGuid, feedbackId, sorting.columnId, sorting.direction]
  );

  const navigateBack = useCallback(() => {
    const euqFeedbackUrl = '/quarantine/euq-feedback';
    navigate(euqFeedbackUrl);
  }, [navigate]);

  // Reset scroll position when component mounts
  useEffect(() => {
    const scrollContainer = document.querySelector('.ant-layout.app-body');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, []);

  return (
    <>
      <Stack minHeight="inherit">
        <Flex height={44} alignItems="center">
          <Button
            variant="ghost"
            columnGap="1x"
            onClick={navigateBack}
            color={colorStyle.color.secondary}
            border="none"
          >
            <Icon as={ChevronLeftIcon} height="4x" fontWeight="bold" />
            <Text size="md">{t('common.action.label.back')}</Text>
          </Button>
        </Flex>
        <Stack gap="6x">
          <Box px="4x" my="4x">
            <EmailHeader
              isLoading={isMailLoading}
              msgSubject={result.msgSubject}
              onViewEmailClick={() => setIsDrawerOpen(true)}
            />
          </Box>
          <AnalysisResults
            isLoading={isResultLoading}
            analysisResult={result.analysisResult}
            summary={result.summary}
            items={result.items}
          />
        </Stack>
        <Divider mb="4x" />

        <Box px="4x" mt="4x" mb="2x">
          <MitigationHeader
            mitigationProgress={result.mitigationProgress}
            mitigationType={result.mitigationType as MitigationType}
            analysisResult={result.analysisResult}
          />
        </Box>

        {tier1PendingCount > 0 && (
          <Box px="4x" mb="4x">
            <MitigationAlert pendingCount={tier1PendingCount} />
          </Box>
        )}

        {mitigationError ? (
          <Stack gap="4x" px="4x">
            <Alert severity="warning" variant="outline">
              {t('operations.euq.mitigation.error')}
            </Alert>
            <MitigationTable
              pagination={pagination}
              onPaginationChange={setPagination}
              sorting={sorting}
              onSortingChange={setSorting}
              height={300}
              showPagination={false}
            />
          </Stack>
        ) : mitigationNotRequired ? (
          <MitigationTable
            pagination={pagination}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
            emptyContent={t('operations.euq.mitigation.notRequired')}
            height={300}
            showPagination={false}
          />
        ) : (
          <>
            {mitigationsAvailable && (
              <Box px="4x" mb="4x">
                <MitigationProgress
                  mitigationProgress={result.mitigationProgress}
                  mitigationDone={mitigationDone}
                  mitigationTotal={mitigationTotal}
                />
              </Box>
            )}
            <Flex flex={1}>
              {mitigationData?.code === 2 ? (
                <Flex
                  flex={1}
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="6x"
                  minHeight={300}
                >
                  <Spinner size="xl" />
                </Flex>
              ) : (
                <MitigationTable
                  data={mitigationData?.contentList || []}
                  isLoading={isMitigationLoading}
                  onExecuteActions={handleExecuteActions}
                  fetchPage={fetchMitigationPage}
                  selectableTotal={selectableTotal}
                  setSelectableTotal={setSelectableTotal}
                  isAutomaticMitigation={
                    (result.mitigationType as MitigationType) ===
                    MitigationType.AUTOMATIC
                  }
                  isExecuting={executeFeedbackActionsMutation.isPending}
                  pagination={pagination}
                  onPaginationChange={setPagination}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  rowCount={mitigationData?.total || 0}
                  companyGuid={companyGuid}
                />
              )}
            </Flex>
          </>
        )}

        <RemediationSection
          isHighConfidence={result.isHighConfidence}
          customRule={result.customRule}
          caseState={result.caseState}
          processingState={result.processingState}
          remediationError={remediationError}
          orgId={companyGuid as string}
        />
      </Stack>

      <EmailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        isLoading={isMailLoading}
        emailData={result}
        formattedTimestamp={formattedTimestamp}
      />
    </>
  );
};
